import { Autocomplete } from "@mui/material";
import BootstrapTextField from "Common/BootstrapTextField";
import SubmitButton from "Components/Buttons/SubmitButton";
import { autocompleteDropdownStyles } from "Constants/Style";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchGstByDropDown } from "slices/gst/thunk";
import {
  addhsnCode,
  updatehsnCode
} from "slices/hsn-code/thunk";
import * as Yup from "yup";

const HSNCodeForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const isEdit = Boolean(id);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const location = useLocation();
  const passedData: any = location.state?.hsnData || null;

  const [gstOptions, setGstOptions] = useState<any[]>([]);
  const [initialData, setInitialData] = useState({
    GST: "",
    HSNCodeName: "",
    HSNDescripton: "",
  });

  // Fetch dropdown + data on edit
  useEffect(() => {
    dispatch(fetchGstByDropDown())
      .unwrap()
      .then((data: any) => {
        setGstOptions(data.data || []);
      })
      .catch(() => toast.error("Failed to fetch GST options"));

    if (isEdit && passedData) {
      setInitialData({
        GST: passedData.gst || "", // should be gst_id
        HSNCodeName: passedData.hsn_code || "",
        HSNDescripton: passedData.hsn_code_description || "",
      });
    }
  }, [id, passedData]);

  // TODO: Replace this with your actual logic to get the logged-in user's UUID
  const userId = localStorage.getItem("userId") || ""; // or get from redux/auth context

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      GST: initialData.GST || "",
      HSNCodeName: initialData.HSNCodeName || "",
      HSNDescripton: initialData.HSNDescripton || "",
    },
    validationSchema: Yup.object({
      GST: Yup.string().required("Please select GST"),
      HSNCodeName: Yup.string()
        .required("Please enter HSN Code")
        .matches(/^\d+$/, "HSN Code must contain numbers only"),
      HSNDescripton: Yup.string().required("Please enter HSN Description"),
    }),
    onSubmit: async (values) => {
      const payload = {
        hsn_code: values.HSNCodeName,
        gst: values.GST,
        hsn_code_description: values.HSNDescripton,
        created_by: userId, // must be a UUID
        updated_by: userId, // must be a UUID
      };

      try {
        if (isEdit) {
          await dispatch(updatehsnCode({ id, ...payload })).unwrap();
          toast.success("HSN Code updated successfully");
        } else {
          await dispatch(addhsnCode(payload)).unwrap();
          toast.success("HSN Code added successfully");
        }
        navigate(-1);
      } catch (error: any) {
        if (error?.errors?.hsn_code) {
          const msg = error.errors.hsn_code[0];
          formik.setFieldError("attribute_name", msg);
          toast.error(msg);
        } else {
          toast.error(error?.message || "Failed to save brand");
        }
      }
    },
  });

  return (
    <div className="page-content px-4">
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          padding: "30px",
          boxShadow: "0 0 15px rgba(0,0,0,0.05)",
        }}
      >
        {isLoading && isEdit ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Form autoComplete="off" onSubmit={formik.handleSubmit}>
            <Row>
              {/* Header */}
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
                <div className="d-flex align-items-center gap-2">
                  <span
                    style={{ fontSize: "24px", cursor: "pointer" }}
                    onClick={() => navigate(-1)}
                  >
                    ←
                  </span>
                  <h5 className="mb-0 fw-bold">
                    {isEdit ? "Edit HSN Code" : "Add HSN Code"}
                  </h5>
                </div>
                <div className="text-muted">
                  <Link
                    to="/hsn-codes"
                  className="text-muted" style={{ fontSize: "0.9rem" }}
                  >
                    HSN Code
                  </Link>{" "}
                  &gt;{" "}
                  <Link
                    to={`/${isEdit ? `edit-hsn-code/${id}` : "add-hsn-code"}`}
                   className="text-muted" style={{ fontSize: "0.9rem" }}
                  >
                    {isEdit ? "Edit" : "Add"} HSN Code
                  </Link>
                </div>
              </div>

              {/* GST Dropdown */}
              <Col lg={6}>
                <div className="mb-4">
                  <Form.Label>
                    GST% <span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    options={gstOptions}
                    getOptionLabel={(option: any) =>
                      typeof option === "string"
                        ? option
                        : `${option.gst_percentage}%`
                    }
                    value={
                      gstOptions.find(
                        (gst: any) => gst.gst_id === formik.values.GST
                      ) || null
                    }
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option: any) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (option: any) =>
                          typeof option === "string"
                            ? option
                            : `${option.gst_percentage}%`
                      )
                    }
                    onChange={(event, selectedGST) => {
                      formik.setFieldValue("GST", selectedGST?.gst_id || "");
                    }}
                    onBlur={() => formik.setFieldTouched("GST", true)}
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder="Select GST"
                        name="GST"
                        error={formik.touched.GST && Boolean(formik.errors.GST)}
                        helperText={formik.touched.GST && formik.errors.GST}
                        fullWidth
                        size="small"
                        sx={autocompleteDropdownStyles.inputSx}
                        onKeyDown={(e: any) => {
                          if (e.key === "Enter") {
                            e.stopPropagation();
                            e.preventDefault();
                          }
                        }}
                      />
                    )}
                  />
                </div>
              </Col>

              {/* HSN Code */}
              <Col lg={6}>
                <div className="mb-3">
                  <Form.Label>
                    HSN Code <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="HSNCodeName"
                    placeholder="Enter HSN Code"
                    value={formik.values.HSNCodeName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.HSNCodeName && formik.touched.HSNCodeName
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.HSNCodeName}
                  </Form.Control.Feedback>
                </div>
              </Col>
            </Row>

            {/* HSN Description */}
            <Row>
              <Col lg={12}>
                <div className="mb-3">
                  <Form.Label>
                    HSN Code Description <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="HSNDescripton"
                    placeholder="Enter Description"
                    value={formik.values.HSNDescripton}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.HSNDescripton &&
                      formik.touched.HSNDescripton
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.HSNDescripton}
                  </Form.Control.Feedback>
                </div>
              </Col>
            </Row>

            {/* Actions */}
            <div className="d-flex justify-content-center gap-3 py-3">
              <Button
                variant="outline-secondary"
                style={{
                  color: "#878899",
                  borderColor: "#B0BAC9",
                  backgroundColor: "transparent",
                }}
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>

              {/* Reusable submit button with spinner */}
              <SubmitButton
                isSubmitting={formik.isSubmitting}
                isEdit={isEdit}
              />
            </div>
          </Form>
        )}
      </div>
    </div>
  );
};

export default HSNCodeForm;
